import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, User, Loader2, Mail, Sun, Moon } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.username || !loginData.password) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      await login(loginData.username, loginData.password);
      toast.success('Giriş başarılı!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-2 bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden p-4 flex items-center justify-between border-b border-border">
        <h1 className="font-heading text-xl font-bold text-primary">ETELEKOM SMS</h1>
        <Button variant="outline" size="icon" onClick={toggleTheme}>
          {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </Button>
      </div>

      {/* Form Section */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8 subtle-grid relative">
        {/* Desktop Theme Toggle */}
        <Button
          variant="outline"
          size="icon"
          onClick={toggleTheme}
          className="absolute top-4 right-4 hidden lg:flex"
        >
          {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </Button>

        <div className="w-full max-w-sm space-y-6 animate-fade-in">
          {/* Logo - Desktop */}
          <div className="hidden lg:block text-center space-y-2">
            <h1 className="font-heading text-3xl font-bold tracking-tight text-primary">
              ETELEKOM SMS
            </h1>
            <p className="text-sm text-muted-foreground">
              Mesaj Onay Yönetim Paneli
            </p>
          </div>

          <Card className="border-border bg-card/80 backdrop-blur-sm">
            <CardHeader className="pb-4 text-center lg:text-left">
              <CardTitle className="font-heading text-xl">Giriş Yap</CardTitle>
              <CardDescription>
                Devam etmek için giriş bilgilerinizi girin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-username">Kullanıcı Adı</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="login-username"
                      data-testid="login-username-input"
                      placeholder="Kullanıcı adınızı girin"
                      className="pl-10 h-12"
                      value={loginData.username}
                      onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Şifre</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="Şifrenizi girin"
                      className="pl-10 h-12"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  className="w-full h-12 text-base"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Giriş yapılıyor...
                    </>
                  ) : (
                    'Giriş Yap'
                  )}
                </Button>
              </form>

              <p className="text-center text-xs text-muted-foreground mt-4">
                Hesap için sistem yöneticisine başvurun
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Desktop Only */}
      <div className="hidden lg:flex relative bg-gradient-to-br from-primary/10 to-primary/5 items-center justify-center">
        <div className="text-center space-y-4 p-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/20 border border-primary/30 backdrop-blur-sm">
            <Mail className="h-10 w-10 text-primary" />
          </div>
          <h2 className="font-heading text-2xl font-semibold">
            Mesaj Kontrolü
          </h2>
          <p className="text-muted-foreground max-w-xs">
            Bekleyen mesajları görüntüleyin, kategorize edin ve onaylayın
          </p>
        </div>
      </div>
    </div>
  );
}
